import { ref } from 'vue'
import type { Api } from '@collection-player/stores/app.store'
import type { CollectionGroup, CollectionFile, CollectionPage } from '@collection-player/types/collection-player.type'
import { isEqual } from 'lodash-es'

const TESTING_WIDTH = 100

export enum AxisDirection {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export default (api: Api) => {
  let oldFirstItem: CollectionPage | CollectionFile | undefined = undefined
  const direction = ref<AxisDirection | undefined>(undefined)

  async function fetchImageAspectRatio(
    documentId: string | undefined,
    fileId: string,
    pageIndex: number,
  ): Promise<number> {
    try {
      const arrayBuffer = await api.renderPageAsImage({
        fileId,
        documentId: documentId ?? undefined,
        pageIndex,
        params: {
          width: TESTING_WIDTH,
        },
      })

      const blob = new Blob([arrayBuffer])
      const img = new Image()

      img.src = URL.createObjectURL(blob)

      return new Promise((resolve, reject) => {
        img.onload = () => {
          resolve(img.width / img.height) // Calculate aspect ratio
        }

        img.onerror = reject
      })
    } catch (error) {
      throw new Error(`Error fetching or processing image: ${error}`)
    }
  }

  const updateSlideDirection = async (groups: CollectionGroup[]) => {
    const isManyGroups = groups.length > 1
    const firstItem = groups[0]?.slides[0]
    const isFirstItemSlide = firstItem && 'slide' in firstItem

    if (isEqual(firstItem, oldFirstItem)) {
      return
    }

    oldFirstItem = firstItem

    if (!isFirstItemSlide || isManyGroups) {
      direction.value = AxisDirection.VERTICAL
      return
    }

    try {
      const aspectRatio = await fetchImageAspectRatio(
        firstItem.file.pspdfkit_document_id ?? undefined,
        firstItem.file.id,
        firstItem.slide.index,
      )

      if (aspectRatio > 1) {
        direction.value = AxisDirection.HORIZONTAL
        return
      }

      direction.value = AxisDirection.VERTICAL
      return
    } catch (error) {
      direction.value = AxisDirection.VERTICAL
      console.error('Error fetching image aspect ratio', error)
    }
  }

  return {
    direction,
    updateSlideDirection,
  }
}
